import { Box, Chip, Typography, Button, Link } from '@mui/material'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab'
import React, { useMemo } from 'react'
import { parseDateAndTime, sortByTimestamp } from '../../helpers/UtilityHelper'
import { useScanViewer, ScanModal } from './orderScanViewer'

function getDotColor(code) {
  if (code === 8) return 'success'
  if ([9, 10, 20].includes(code)) return 'error'
  return 'primary'
}

const PTOrderTimeline = ({ orderDetailInfo, cancelledBy, awb }) => {
  const events = useMemo(() => {
    if (!orderDetailInfo?.pt_order_history?.length) return []
    return sortByTimestamp(orderDetailInfo.pt_order_history, 'timestamp')
  }, [orderDetailInfo?.pt_order_history])

  const { showAllScans, showItemScan, modalProps } = useScanViewer(awb)

  if (events.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontSize: 13 }}>
        No PT tracking events recorded yet.
      </Box>
    )
  }

  return (
    <Box
      sx={{
        background: '#fafafa',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1,
        maxHeight: 520,
        overflowY: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 0.5 }}>
        <Button
          size='small'
          variant='outlined'
          startIcon={<ReceiptLongOutlinedIcon />}
          onClick={showAllScans}
          sx={{ fontSize: 11.5 }}
        >
          Show all scans
        </Button>
      </Box>
      <Timeline
        sx={{
          m: 0,
          p: 0,
          '& .MuiTimelineItem-root:before': { display: 'none' },
        }}
      >
        {events.map((item, index) => {
          const isLatest = index === 0
          const isLast = index === events.length - 1
          const color = getDotColor(item.orderStatusCode)
          return (
            <TimelineItem key={index}>
              <TimelineSeparator>
                <TimelineDot
                  color={color}
                  variant={isLatest ? 'filled' : 'outlined'}
                  sx={{
                    my: 0.5,
                    boxShadow: isLatest ? `0 0 0 4px rgba(59,130,246,0.15)` : 'none',
                  }}
                />
                {!isLast && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent sx={{ py: 0.5, pb: 2, pl: 1.5 }}>
                <Typography
                  sx={{
                    fontSize: 10.5,
                    color: 'text.secondary',
                    fontWeight: 500,
                    lineHeight: 1.2,
                    mb: 0.25,
                  }}
                >
                  {parseDateAndTime(item.timestamp)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                  <Link
                    component='button'
                    type='button'
                    underline='hover'
                    onClick={() => showItemScan(item)}
                    title='View the raw courier scan behind this status'
                    sx={{
                      textAlign: 'left',
                      fontWeight: isLatest ? 700 : 600,
                      fontSize: isLatest ? 14 : 13,
                      color: '#2065D1',
                      lineHeight: 1.3,
                      cursor: 'pointer',
                      '&:hover': { color: '#103996' },
                    }}
                  >
                    {item.orderStatusDescription}
                  </Link>
                  {isLatest && (
                    <Chip
                      label='Current'
                      size='small'
                      color='primary'
                      sx={{ height: 18, fontSize: 9.5, fontWeight: 600 }}
                    />
                  )}
                </Box>
                {item.currentLocation && (
                  <Typography
                    sx={{
                      display: 'block',
                      fontSize: 12,
                      color: 'text.secondary',
                      lineHeight: 1.4,
                    }}
                  >
                    📍 {item.currentLocation}
                  </Typography>
                )}
                {item.remark && (
                  <Typography
                    sx={{
                      display: 'block',
                      fontSize: 12,
                      color: 'text.secondary',
                      lineHeight: 1.4,
                    }}
                  >
                    {item.remark}
                  </Typography>
                )}
                {cancelledBy && item.orderStatusCode === 10 && (
                  <Typography
                    sx={{
                      display: 'block',
                      fontSize: 11.5,
                      color: 'error.main',
                      fontWeight: 600,
                      mt: 0.25,
                    }}
                  >
                    Cancelled by: {cancelledBy}
                  </Typography>
                )}
                {item.creationDate && (
                  <Typography
                    sx={{
                      display: 'block',
                      fontSize: 10,
                      color: 'text.disabled',
                      mt: 0.25,
                    }}
                  >
                    Logged {parseDateAndTime(item.creationDate)}
                  </Typography>
                )}
              </TimelineContent>
            </TimelineItem>
          )
        })}
      </Timeline>

      <ScanModal {...modalProps} />
    </Box>
  )
}

export default PTOrderTimeline
